import React, { useEffect, useReducer, useRef, useState } from "react";
import { Button, Col, Modal, Row } from "react-bootstrap";
import axios from "src/config/axios";
import { Form, Formik } from "formik";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FormColorSelect } from "../../../../../components/UiComponents/ColorSelect";
import { FormIconSelect } from "../../../../../components/UiComponents/IconSelect";
import MultiLanguageInputText from "../../../../../components/Forms/MultiLanguageInputText";
import ParkingSlotCategoryPreview from "src/components/ParkingSlotCategoryPreview";
import FormFileUpload from "src/components/FileUpload";
import moment from "moment";
import FormCheckbox from "src/components/Forms/FormCheckbox";
import FormSelectBox from "src/components/Forms/FormSelectBox";
import { useTranslation } from "react-i18next";

export default function EditParkingCategoryDialog({
  show,
  toggle,
  category_id,
  handleSuccess = null,
}) {
  function validate(values) {
    const errors = {};

    if (values.name == null || values.name.trim() === "") {
      errors.name = "Name darf nicht leer sein.";
    }

    return errors;
  }

  const { t } = useTranslation("dashboard");
  const formRef = useRef();
  const [showPreview, setShowPreview] = useState(true);

  const [parkingSlotCategory, setParkingSlotCategory] = useState();
  const [parkingSlotCategoryTemplate, setParkingSlotCategoryTemplate] =
    useState({ settings: {} });
  const [previewParkingSlotCategory, setPreviewParkingSlotCategory] =
    useState();
  const [
    previewParkingSlotCategorySelected,
    setPreviewParkingSlotCategorySelected,
  ] = useState(false);

  const minReservationDaysOptions = [
    {
      name: t("parking_slot_category.min_reservation_days.none"),
      value: 0,
    },
    ...Array.from({ length: 7 }, (_, index) => {
      const days = index + 1;

      return {
        name: t("parking_slot_category.min_reservation_days.day", {
          count: days,
        }),
        value: days,
      };
    }),
  ];

  useEffect(() => {
    fetchParkingSlotCategoryTemplate();
  }, [category_id]);

  const fetchParkingSlotCategory = () => {
    axios
      .get(`api/v1/parking_slot_categories/${category_id}.json`)
      .then((response) => {
        setParkingSlotCategory(response.data);
        setPreviewParkingSlotCategory(response.data);
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  const fetchParkingSlotCategoryTemplate = () => {
    axios
      .get(`/api/v1/parking_slot_categories/template.json`)
      .then((response) => {
        setParkingSlotCategoryTemplate(response.data);

        if (category_id) {
          fetchParkingSlotCategory();
        } else {
          setParkingSlotCategory(response.data);
          setPreviewParkingSlotCategory(response.data);
        }
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  if (!show) {
    return <></>;
  }

  const valuesToFormData = (
    values,
    objectName,
    dateNames = [],
    fileNames = []
  ) => {
    var formData = new FormData();

    for (let [key, value] of Object.entries(values)) {
      if (dateNames.includes(key)) {
        formData.append(
          `${objectName}[${key}]`,
          moment(value).format(moment.HTML5_FMT.DATE)
        );
      } else if (fileNames.includes(key) && !(value instanceof File)) {
      } else {
        formData.append(`${objectName}[${key}]`, value);
      }
    }

    return formData;
  };

  function handleHide() {
    setPreviewParkingSlotCategory({ ...parkingSlotCategory });
    toggle?.();
  }

  function submit(values, { setSubmitting }) {
    setSubmitting(true);
    const valuesAsFormData = valuesToFormData(
      values,
      "parking_slot_category",
      [],
      ["image"]
    );

    if (category_id) {
      axios
        .put(
          `/api/v1/parking_slot_categories/${category_id}.json`,
          valuesAsFormData
        )
        .then(function () {
          if (handleSuccess) {
            handleSuccess();
          }
          toggle();
        })
        .catch(function (e) {
          console.error(e);
        });
    } else {
      axios
        .post(`/api/v1/parking_slot_categories`, valuesAsFormData)
        .then(function () {
          if (handleSuccess) {
            handleSuccess();
          }
          toggle();
        })
        .catch(function (e) {
          console.error(e);
        });
    }
  }

  function handleFileSelect(file) {
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewParkingSlotCategory({
          ...previewParkingSlotCategory,
          imageBase64: reader.result,
        });
      };
      reader.readAsDataURL(file);
    } else {
      setPreviewParkingSlotCategory({
        ...previewParkingSlotCategory,
        image: null,
        imageBase64: null,
      });
    }
  }

  function handleIconChange(value, setFieldValue) {
    setFieldValue("icon", value);
    setPreviewParkingSlotCategory({
      ...previewParkingSlotCategory,
      icon: value,
    });
  }

  function handleColorChange(value, setFieldValue) {
    setFieldValue("color_name", value);
    const color = parkingSlotCategoryTemplate.settings.colors.find(
      (color) => color.id == value
    );
    setPreviewParkingSlotCategory({
      ...previewParkingSlotCategory,
      color_name: value,
      color: {
        ...previewParkingSlotCategory.color,
        value: color.value,
      },
    });
  }

  return (
    <Modal
      show={true}
      className="p-0 m-0 edit-parking-slot-category-modal"
      dialogClassName="dialog"
      onHide={handleHide}
    >
      <Modal.Header>
        <Modal.Title>
          {category_id
            ? `Kategorie "${parkingSlotCategory.name}" bearbeiten`
            : "Kategorie erstellen"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0 w-100">
        <Row className="m-0 p-0">
          <Col>
            <Formik
              innerRef={formRef}
              initialValues={parkingSlotCategory}
              enableReinitialize={true}
              validate={validate}
              onSubmit={submit}
            >
              {({ values, setFieldValue }) => (
                <Form>
                  <Row>
                    <Col>
                      <div className="pt-4 p-1 d-flex flex-wrap">
                        <div className={"ml-auto"}>
                          <button
                            onClick={() => setShowPreview(!showPreview)}
                            type={"button"}
                            className="btn bg-white border d-flex align-items-center justify-content-center mb-3"
                            style={{ height: "2.5rem" }}
                          >
                            <FontAwesomeIcon
                              icon={faEyeSlash}
                              className="mr-1"
                            />
                            <span className="pl-1">
                              {showPreview
                                ? "Vorschau anzeigen"
                                : "Vorschau ausblenden"}
                            </span>
                          </button>
                        </div>
                        <div className="w-100">
                          <MultiLanguageInputText
                            name="name"
                            label="Kategorie Name"
                            languages={["de", "it", "en"]}
                            onVisibleValueChange={(visibleValue) => {
                              setPreviewParkingSlotCategory({
                                ...previewParkingSlotCategory,
                                name: visibleValue,
                              });
                            }}
                          />
                        </div>
                        <div className="w-100">
                          <MultiLanguageInputText
                            name="description"
                            label="Beschreibung"
                            placeholder="z.B. Stromanschluss; Platz für 2 Personen"
                            as="textarea"
                            languages={["de", "it", "en"]}
                            onVisibleValueChange={(visibleValue) => {
                              setPreviewParkingSlotCategory({
                                ...previewParkingSlotCategory,
                                description: visibleValue,
                              });
                            }}
                          />

                          <FormFileUpload
                            name="image"
                            label="Bild"
                            onFileSelect={handleFileSelect}
                          />
                        </div>
                        <div className="w-100">
                          <FormCheckbox
                            name="available_for_reservations"
                            label="Für Reservierungen verfügbar"
                            className="mb-3"
                          />
                        </div>
                        {values.available_for_reservations && (
                          <div className="w-100">
                            <FormSelectBox
                              required
                              name="min_reservation_days"
                              options={minReservationDaysOptions}
                              label={t(
                                "parking_slot_category.min_reservation_days.label"
                              )}
                              hint={t(
                                "parking_slot_category.min_reservation_days.hint"
                              )}
                            />
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <hr className="w-100" style={{ borderTopWidth: "2px" }} />
                    </Col>
                  </Row>
                  <Row className="pb-3">
                    <Col>
                      <FormIconSelect
                        name="icon"
                        editMode={true}
                        color={values.color.value}
                        onChange={(value) =>
                          handleIconChange(value, setFieldValue)
                        }
                      />
                    </Col>
                    <Col>
                      <FormColorSelect
                        name="color_name"
                        colors={parkingSlotCategoryTemplate.settings.colors}
                        onChange={(value) =>
                          handleColorChange(value, setFieldValue)
                        }
                      />
                    </Col>
                  </Row>
                </Form>
              )}
            </Formik>
          </Col>
          {showPreview && (
            <Col className="p-0 m-0 preview-container">
              {previewParkingSlotCategory.name && (
                <div className="border-left p-2">
                  <ParkingSlotCategoryPreview
                    className="parking-slot-category-preview-vertical"
                    parkingSlotCategory={previewParkingSlotCategory}
                    selected={previewParkingSlotCategorySelected}
                    onClick={() => {
                      setPreviewParkingSlotCategorySelected(
                        !previewParkingSlotCategorySelected
                      );
                    }}
                  />
                </div>
              )}
            </Col>
          )}
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={toggle}>
          Schließen
        </Button>
        <Button
          variant="primary"
          onClick={() => formRef?.current?.handleSubmit()}
        >
          {category_id ? "Speichern" : "Erstellen"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
